import { subscriptionStatusBadgeClass, subscriptionStatusLabel } from '../subscriptionStatus';
import type { BillingStatusResponse } from '../billingTypes';
import { formatCents, formatUtcDate } from './money';
import { daysLeftLabel } from '../formatDate';
import type { CompanySubscriptionResponse, PlanSummary } from '../types';

/**
 * Current plan + state, and the next-bill estimate. Every value is what the server returned: the state text is
 * `effectiveState`, the dates are the server's, and the estimate is the server's `nextInvoiceEstimate` (base fee plus
 * per-employee fee times the billable headcount). The browser never works out a date or a total itself.
 */
export function SummarySection({
  subscription, status, plans,
}: {
  subscription: CompanySubscriptionResponse | null;
  status: BillingStatusResponse | null;
  plans: PlanSummary[];
}) {
  const estimate = status?.nextInvoiceEstimate ?? null;
  const pendingPlan = subscription?.pendingPlanCode ? plans.find((p) => p.code === subscription.pendingPlanCode) : null;
  const state = subscription?.effectiveState;
  const isPaid = state === 'Active' || state === 'PendingChange' || state === 'PastDueGrace' || state === 'PastDueRestricted';

  return (
    <section aria-labelledby="billing-plan-heading" className="billing-section" data-testid="billing-summary">
      <h3 id="billing-plan-heading">Your plan</h3>

      {!subscription && (
        <p>You do not have a plan yet. Choose one below to start a free trial or subscribe.</p>
      )}

      {subscription && (
        <>
          <p>
            <strong>{subscription.planName}</strong>{' '}
            <span className={subscriptionStatusBadgeClass(subscription)} data-testid="billing-state">{subscriptionStatusLabel(subscription)}</span>
          </p>

          <dl className="billing-facts">
            <div><dt>Base monthly fee</dt><dd>{formatCents(subscription.baseMonthlyFeeCents)}</dd></div>
            <div><dt>Per billable employee</dt><dd>{formatCents(subscription.perEmployeeMonthlyFeeCents)}</dd></div>
            {state === 'TrialActive' && (
              <div>
                <dt>Trial ends</dt>
                <dd>{formatUtcDate(subscription.trialEndUtc)}{daysLeftLabel(subscription.trialDaysRemaining) ? ` (${daysLeftLabel(subscription.trialDaysRemaining)})` : ''}</dd>
              </div>
            )}
            {isPaid && status?.nextBillingUtc && !subscription.cancelAtPeriodEnd && (
              <div><dt>Next billing date</dt><dd>{formatUtcDate(status.nextBillingUtc)}</dd></div>
            )}
            {isPaid && subscription.cancelAtPeriodEnd && (
              <div><dt>Ends on</dt><dd>{formatUtcDate(subscription.currentPeriodEndUtc)}</dd></div>
            )}
            {subscription.paymentFailed && subscription.graceEndsUtc && state === 'PastDueGrace' && (
              <div><dt>Plan access continues until</dt><dd>{formatUtcDate(subscription.graceEndsUtc)}</dd></div>
            )}
          </dl>

          {state === 'PastDueGrace' && (
            <p role="status">
              Your last payment did not go through. Update your payment method below; your plan access continues until{' '}
              {formatUtcDate(subscription.graceEndsUtc)}.
            </p>
          )}
          {state === 'PastDueRestricted' && (
            <p role="status">
              Your last payment did not go through, so the company is read-only until the payment is collected. Update your
              payment method below.
            </p>
          )}
          {state === 'Canceled' && (
            <p role="status">This subscription is canceled and the company is read-only. You can reactivate it below. Your records are kept.</p>
          )}
          {isPaid && subscription.cancelAtPeriodEnd && (
            <p role="status">
              Cancellation takes effect at the end of the current billing period, on {formatUtcDate(subscription.currentPeriodEndUtc)}.
              You keep full access until then.
            </p>
          )}
          {subscription.pendingPlanCode && (
            <p role="status" data-testid="pending-plan-change">
              Scheduled change: switching to <strong>{pendingPlan?.name ?? subscription.pendingPlanCode}</strong> takes effect at the
              end of the current billing period, on {formatUtcDate(subscription.pendingPlanEffectiveUtc)}. You keep{' '}
              {subscription.planName} until then.
            </p>
          )}
        </>
      )}

      {estimate && (
        <div className="billing-estimate" data-testid="billing-estimate">
          <h4>{isPaid ? 'Estimated next bill' : 'Estimated monthly bill after you subscribe'}</h4>
          <dl className="billing-facts">
            <div><dt>Base fee</dt><dd>{formatCents(estimate.baseCents)}</dd></div>
            <div>
              <dt>{estimate.billableEmployees} billable {estimate.billableEmployees === 1 ? 'employee' : 'employees'} × {formatCents(estimate.perEmployeeCents)}</dt>
              <dd>{formatCents(estimate.perEmployeeCents * estimate.billableEmployees)}</dd>
            </div>
            <div className="billing-facts__total"><dt>Estimated total</dt><dd>{formatCents(estimate.estimatedTotalCents)}</dd></div>
          </dl>
          <p className="muted">{estimate.taxNote ?? 'Taxes are shown at checkout.'}</p>
        </div>
      )}
    </section>
  );
}
